import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from '../utils/apiClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token and get user data
      verifyToken();
    } else {
      // Ensure no user is set when there's no token
      setUser(null);
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await apiClient.get('/auth/verify');
      if (response.data.valid && response.data.user) {
        // Ensure user has required fields before setting as logged in
        const user = response.data.user;
        if (user.id && user.username && user.email && user.role) {
          setUser(user);
        } else {
          console.warn('Invalid user data received, logging out');
          localStorage.removeItem('token');
          setUser(null);
        }
      } else {
        localStorage.removeItem('token');
        setUser(null);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await apiClient.post('/auth/login', credentials);
      const { user, token } = response.data;
      
      localStorage.setItem('token', token);
      setUser(user);
      
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await apiClient.post('/auth/register', userData);
      const { user } = response.data;
      
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Registration failed' 
      };
    }
  };

  const logout = async () => {
    // Add a brief loading state for smooth transition
    setLoading(true);
    
    // Try to call logout endpoint (but don't fail if it errors - token might be expired)
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await apiClient.post('/auth/logout');
      }
    } catch (error) {
      // Ignore errors - token might be expired or invalid, which is fine for logout
      console.log('Logout API call failed (this is OK if token expired):', error.message);
    }
    
    // Clear all authentication data regardless of API call result
    localStorage.removeItem('token');
    setUser(null);
    
    // Remove loading state after a brief delay for smooth transition
    setTimeout(() => {
      setLoading(false);
    }, 100);
  };

  const clearAuth = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const isAdmin = () => {
    return user && user.role === 'ADMIN';
  };

  const handleOAuthCallback = async (token) => {
    try {
      // Store the token
      localStorage.setItem('token', token);
      
      // Verify token and get user data
      const response = await apiClient.get('/auth/verify');
      if (response.data.valid && response.data.user) {
        const user = response.data.user;
        if (user.id && user.username && user.email && user.role) {
          setUser(user);
          return { success: true, user };
        } else {
          console.warn('Invalid user data received');
          localStorage.removeItem('token');
          setUser(null);
          return { success: false, error: 'Invalid user data' };
        }
      } else {
        localStorage.removeItem('token');
        setUser(null);
        return { success: false, error: 'Token verification failed' };
      }
    } catch (error) {
      console.error('OAuth callback error:', error);
      localStorage.removeItem('token');
      setUser(null);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Authentication failed' 
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    clearAuth,
    isAdmin,
    handleOAuthCallback
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
